import { useEffect, useState, useRef } from "react";
import EmployeePermissionService from "../services/EmployeePermissionService";
import UserService from "../services/EmployeeService";
import StorageService from "../services/StorageService";
import Breadcrumb from "../components/Breadcrumb";
import MessageDialog from "../components/MessageDialog";


export default function EmployeePermission() {

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [screenSearch, setScreenSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  // const messageTimer = useRef(null);

  const loggedUser = StorageService.loginData();

  // ✅ Message
  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 3000);
  };

  // const closeMessage = () => setMessage("");

  // ✅ Load employees
  useEffect(() => {
    const loadEmployees = async () => {
      const activeCompany = StorageService.activeCompany();
      const res = await UserService.getAllUsers(activeCompany.companyId);
      setEmployees(Array.isArray(res.data) ? res.data : []);
      setFilteredEmployees(Array.isArray(res.data) ? res.data : []);
    };
    loadEmployees();
  }, []);

  // ✅ Filter employees
  useEffect(() => {
    if (!search) {
      setFilteredEmployees(employees);
    } else {
      const filtered = employees.filter(emp =>
        `${emp.firstName || ""} ${emp.lastName || ""}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  }, [search, employees]);

  // ✅ Select employee
  const handleSelectEmployee = async (emp) => {
    setSelectedUser(emp.id);
    setSearch(`${emp.firstName} ${emp.lastName}`);
    setShowDropdown(false);

    const screens = await EmployeePermissionService.getScreens();
    const existingPerms = await EmployeePermissionService.getPermissionsByUser(emp.id);

    const merged = screens.map((screen) => {
      const existing = existingPerms.find(
         p => Number(p.screenId) === Number(screen.id)
      );

      return {
        screenId: screen.id,
        screenName: screen.screenName,
        canView: Number(existing?.canView || 0),
        canAdd: Number(existing?.canAdd || 0),
        canModify: Number(existing?.canModify || 0),
        canCancel: Number(existing?.canCancel || 0),
        canApprove: Number(existing?.canApprove || 0),
        canReject: Number(existing?.canReject || 0),
        canTransfer: Number(existing?.canTransfer || 0),
        canEsclate: Number(existing?.canEsclate || 0),
        allowNotification: Number(existing?.allowNotification || 0),
      };
    });

    setPermissions(merged);
  };

  // ✅ Close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fields = [
    "canView",
    "canAdd",
    "canModify",
    "canCancel",
    "canApprove",
    "canReject",
    "canTransfer",
    "canEsclate",
    "allowNotification"
  ];

  // ✅ Checkbox change
  const handleChange = (screenId, field) => {
    const updated = permissions.map((p) => {
      if (p.screenId === screenId) {
        return {
          ...p,
          [field]: Number(p[field]) === 1 ? 0 : 1
        };
      }
      return p;
    });
    setPermissions(updated);
  };

  // ✅ Column selection change
  const handleColumnChange = (field, value) => {
    const updated = permissions.map((row) => ({
      ...row,
      [field]: value ? 1 : 0
    }));
    setPermissions(updated);
  };

  // ✅ Check if column is checked for all screens
  const isColumnChecked = (field) => {
    return permissions.length > 0 && permissions.every(row => Number(row[field]) === 1);
  };

  // ✅ Row selection helpers
  const isRowAllChecked = (row) => {
    return fields.every(field => Number(row[field]) === 1);
  };

  const handleRowToggle = (screenId, checked) => {
    const value = checked ? 1 : 0;
    const updated = permissions.map((p) => {
      if (p.screenId === screenId) {
        const updatedRow = { ...p };
        fields.forEach(field => {
          updatedRow[field] = value;
        });
        return updatedRow;
      }
      return p;
    });
    setPermissions(updated);
  };

  // ✅ Grid selection helpers
  const isGridAllChecked = () => {
    return permissions.length > 0 && permissions.every(row =>
      fields.every(field => Number(row[field]) === 1)
    );
  };

  const handleGridToggle = (checked) => {
    const value = checked ? 1 : 0;
    const updated = permissions.map((row) => {
      const updatedRow = { ...row };
      fields.forEach(field => {
        updatedRow[field] = value;
      });
      return updatedRow;
    });
    setPermissions(updated);
  };

  // ✅ Filtered permissions for view/search
  const filteredPermissions = permissions.filter(perm =>
    (perm.screenName || "").toLowerCase().includes(screenSearch.toLowerCase())
  );

  // ✅ Save
  const handleSave = async () => {
    try {
      if (!selectedUser || permissions.length === 0) {
        showMessage("Please select a user and update permissions before saving.", "error");
        return;
      }

      setLoading(true);

      await EmployeePermissionService.savePermissions({
        userId: Number(selectedUser),
        updatedBy: Number(loggedUser.employeeId),
        permissions: permissions.map((perm) => ({
          ...perm,
          screenId: Number(perm.screenId),
          canView: Number(perm.canView),
          canAdd: Number(perm.canAdd),
          canModify: Number(perm.canModify),
          canCancel: Number(perm.canCancel),
          canApprove: Number(perm.canApprove),
          canReject: Number(perm.canReject),
          canTransfer: Number(perm.canTransfer),
          canEsclate: Number(perm.canEsclate),
          allowNotification: Number(perm.allowNotification)
        }))
      });

      showMessage("Permissions saved successfully", "success");
    } catch (err) {
      console.error("Save permission error:", err);
      showMessage("Error saving permissions", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageTitle="Employee Permission" secondTitle="Employee Management" />

      {/* Message */}
      {/* {message && (
        <div className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"} d-flex justify-content-between`}>
          <span>{message}</span>
          <button className="btn-close" onClick={closeMessage}></button>
        </div>
      )} */}

      
        <MessageDialog
          message={message}
          type={msgType}
          setMessage={setMessage}
          setMsgType={setMsgType}
        />
      

      <div className="card shadow-sm">
        <div className="card-header">
          <h5 className="text-info">Employee Permission</h5>
        </div>

        <div className="card-body position-relative">

          {/* ✅ LOADER */}
          {loading && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
              style={{
                background: "rgba(255,255,255,0.7)",
                zIndex: 10
              }}
            >
              <div className="spinner-border text-primary"></div>
            </div>
          )}

          {/* ✅ DISABLE UI */}
          <div className={loading ? "opacity-50 pointer-events-none" : ""}>

            {/* 🔥 Search + Dropdown */}
            <div className="position-relative mb-3" ref={dropdownRef}>
              <input
                type="text"
                className="form-control"
                placeholder="Search Employee..."
                value={search}
                onFocus={() => setShowDropdown(true)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowDropdown(true);
                }}
              />

              {showDropdown && (
                <div className="dropdown-menu show w-100" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map(emp => (
                      <button
                        key={emp.id}
                        className="dropdown-item"
                        onClick={() => handleSelectEmployee(emp)}
                      >
                        {emp.firstName} {emp.lastName}
                      </button>
                    ))
                  ) : (
                    <span className="dropdown-item text-muted">No results</span>
                  )}
                </div>
              )}
            </div>

            {/* 🔥 Table */}
            {selectedUser && (
              <>
                {/* 🔍 Search Screens */}
                <div className="mb-3 d-flex justify-content-between align-items-center gap-3">
                  <div className="flex-grow-1" style={{ maxWidth: "400px" }}>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="ti ti-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search screens..."
                        value={screenSearch}
                        onChange={(e) => setScreenSearch(e.target.value)}
                      />
                      {screenSearch && (
                        <button
                          className="btn btn-outline-secondary border-start-0"
                          type="button"
                          onClick={() => setScreenSearch("")}
                        >
                          <i className="ti ti-x"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-muted small">
                    Showing {filteredPermissions.length} of {permissions.length} screens
                  </div>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Screen</th>
                        <th className="text-center" style={{ minWidth: "100px" }}>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <input
                              type="checkbox"
                              checked={isGridAllChecked()}
                              onChange={(e) => handleGridToggle(e.target.checked)}
                            />
                            <span>All</span>
                          </div>
                        </th>

                        {[
                          { label: "View", field: "canView" },
                          { label: "Add", field: "canAdd" },
                          { label: "Modify", field: "canModify" },
                          { label: "Cancel", field: "canCancel" },
                          { label: "Approve", field: "canApprove" },
                          { label: "Reject", field: "canReject" },
                          { label: "Transfer", field: "canTransfer" },
                          { label: "Escalate", field: "canEsclate" },
                          { label: "Allow Notification", field: "allowNotification" }
                        ].map(({ label, field }) => (
                          <th key={field} className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <input
                                type="checkbox"
                                checked={isColumnChecked(field)}
                                onChange={(e) => handleColumnChange(field, e.target.checked)}
                              />
                              <span>{label}</span>
                            </div>
                          </th>
                        ))}

                      </tr>
                    </thead>

                    <tbody>
                      {filteredPermissions.length > 0 ? (
                        filteredPermissions.map((row) => (
                          <tr key={row.screenId}>
                            <td>{row.screenName}</td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={isRowAllChecked(row)}
                                onChange={(e) => handleRowToggle(row.screenId, e.target.checked)}
                              />
                            </td>

                            {[
                              "canView",
                              "canAdd",
                              "canModify",
                              "canCancel",
                              "canApprove",
                              "canReject",
                              "canTransfer",
                              "canEsclate",
                              "allowNotification"
                            ].map((field) => (
                              <td key={field} className="text-center">
                                <input
                                  type="checkbox"
                                  checked={Number(row[field]) === 1}
                                  onChange={() => handleChange(row.screenId, field)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="text-center text-muted py-3">
                            No screens found matching "{screenSearch}"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="text-end mt-3">
                  <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? "Saving..." : "Save Permissions"}
                  </button>
                </div>
              </>
            )}

          </div>

        </div>
      </div>
    </>
  );
}